'use client';
import { createContext } from 'react';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { DeliveryType } from '@/features/enums/DeliveryType';

export type CartTipType = 'percentage' | 'fixed' | 'custom';

export type CartTipSelection = {
    enabled: boolean;
    type: CartTipType;
    value: string;
};

export type CartFormValues = {
    bonusPoints: [number];
    items: {
        hash: string;
        quantity: number;
        variant?: string;
        addons?: Record<string, string | Record<string, number>>;
        /** Member item id → item_variant row id, only when that variant is not the default. */
        memberVariants?: Record<string, string>;
        note?: string;
    }[];
    deliveryMethod: {
        id: string | null;
        // Last known type of the selected method; used to pick a same-type
        // fallback when the selected method disappears from the list
        type: DeliveryType | null;
        address: {
            city: string;
            country: string;
            postCode: string;
            street: string;
            lat: string;
            lon: string;
        } | null;
        addressId: string | null;
        manual: boolean;
    };
    paymentMethod: {
        id: string | null;
    };
    time: {
        type: 'earliest' | 'timed';
        timed: {
            date?: string;
            timeStart?: string;
        } | null;
    };
    deliveryTime: string | null;
    contactInfo: {
        name: string;
        surname: string;
        telephone: string;
        telephoneVerified: boolean;
        email: string;
    };
    addCompanyInfo: boolean;
    company?: {
        name: string;
        address: {
            city: string;
            country: string;
            postCode: string;
            street: string;
        } | null;
        cin: string;
        vat: string;
        issueInvoice: boolean;
    };
    note: string;
    privacyPolicyAgreement: boolean;
    newsletterAgreement: boolean;
    tips: Record<string, CartTipSelection>;
    topUpOption: 'pay' | 'select-more';
    saveForNextOrder: boolean;
    _isHydrated: boolean;
    /** True after successful checkout while leaving the cart (not persisted). */
    _isOrderCreated: boolean;
};

type CartContextValue = {
    formMethods: UseFormReturn<CartFormValues>;
    getCartFormDefaultValues: (prevFormValues?: Partial<CartFormValues>) => CartFormValues;
    itemsFieldArray: UseFieldArrayReturn<CartFormValues, 'items', '_id'>;
};

export type CartItemsFields = UseFieldArrayReturn<CartFormValues, 'items', '_id'>['fields'];

export const CartContext = createContext<CartContextValue>({} as CartContextValue);

/** Live field-array rows (incl. `_id`). Separate from CartContext so qty/note edits
 *  don't re-render every cart consumer — only lists that subscribe here. */
export const CartItemsFieldsContext = createContext<CartItemsFields>([]);
